import type { ComponentProps, ReactNode } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type { ResponsiveValue } from 'styled-system';
import { margin, size } from 'styled-system';

import { useLocalization } from 'common/providers/LocaleProvider';
import type { Color, ColorScheme } from '../../theme/colors/types';
import { boxSizing } from '../../theme/system-utilities';
import { LinkButton } from '../Button/LinkButton';
import { Flex } from '../Flex';
import InfoButton from '../InfoButton';
import { LinkRegularLR3 } from '../Typography';
import { useDisclaimerDrawer } from './hooks/use-disclaimer-drawer';

interface LegalDisclaimerProps {
  label: string | ReactNode;
  content: string | ReactNode;
  color: Nullable<string>;
  withIcon?: boolean;
  ariaLabel?: string;
  withNoMargin?: boolean;
  iconColorScheme?: ColorScheme;
  iconSize?: ResponsiveValue<string | number>;
  buttonSize?: ResponsiveValue<string | number>;
  onClick?: () => void;
  justifyContent?: ResponsiveValue<string>;
  linkComponent?: React.ElementType;
}

const DisclaimerContainer = styled(Flex)<any>`
  ${boxSizing};
`;

const disclaimerContainerStyles: ComponentProps<typeof DisclaimerContainer> = {
  boxSizing: 'border-box',
  justifyContent: 'flex-end',
};

const Info = styled(InfoButton)<any>`
  ${size};
  ${margin}
`;

const LegalDisclaimer = ({
  color: disclaimerColor,
  content,
  label,
  withIcon,
  ariaLabel,
  iconSize,
  buttonSize,
  withNoMargin,
  iconColorScheme,
  onClick,
  linkComponent: LinkComponent = LinkRegularLR3,
  ...props
}: LegalDisclaimerProps) => {
  const { t } = useLocalization();
  const onClickHandler = useDisclaimerDrawer({ content, onClick });

  return (
    <DisclaimerContainer data-testid="legal-disclaimer" {...disclaimerContainerStyles} {...props}>
      {withIcon ? (
        <Info
          size={buttonSize}
          ariaLabel={ariaLabel ?? t('h24_common_disclaimer_aria_label')}
          colorScheme={iconColorScheme}
          onClick={onClickHandler}
          iconProps={{
            ...(iconSize && { size: iconSize }),
            ...(disclaimerColor && { fill: disclaimerColor }),
          }}
          {...(withNoMargin ? { m: 'sp_0' } : {})}
        />
      ) : (
        <LinkButton color={disclaimerColor as Color} onClick={onClickHandler}>
          <LinkComponent
            data-testid="disclaimer-link"
            color={disclaimerColor as Color}
            {...(ariaLabel && { 'aria-label': ariaLabel })}
          >
            {label}
          </LinkComponent>
        </LinkButton>
      )}
    </DisclaimerContainer>
  );
};

export default LegalDisclaimer;
